import { computed, type ComputedRef } from 'vue';
import { useHead } from '@unhead/vue';
import type { Experience } from '@experience/exp-card/dist/types/experience';

type SchemaPrimitive = string | number;

interface SchemaObject {
  [key: string]: SchemaPrimitive | SchemaObject | SchemaObject[] | undefined;
}

const getAbsoluteUrl = (url?: string) => {
  if (!url || /^https?:\/\//.test(url)) {
    return url;
  }

  if (typeof window === 'undefined' || !window.location?.origin) {
    return url;
  }

  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  return `${window.location.origin}${normalizedUrl}`;
};

const stringifyJsonLd = (value: SchemaObject) => {
  return JSON.stringify(value)
    .replaceAll('<', '\\u003C')
    .replaceAll('>', '\\u003E')
    .replaceAll('&', '\\u0026')
    .replaceAll('\u2028', '\\u2028')
    .replaceAll('\u2029', '\\u2029');
};

const buildOffer = (exp: Experience): SchemaObject | undefined => {
  if (!Number.isFinite(exp.price.value)) {
    return undefined;
  }

  return {
    '@type': 'Offer',
    price: exp.price.value,
    priceCurrency: exp.price.currency,
  };
};

const buildAggregateRating = (exp: Experience): SchemaObject | undefined => {
  if (!exp.rating || !exp.review_count) {
    return undefined;
  }

  return {
    '@type': 'AggregateRating',
    ratingValue: exp.rating,
    reviewCount: exp.review_count,
  };
};

const buildProduct = (exp: Experience): SchemaObject => {
  return {
    '@type': 'Product',
    name: exp.title,
    description: exp.annotation || exp.tagline,
    image: exp.photos?.[0]?.medium,
    url: getAbsoluteUrl(exp.url),
    offers: buildOffer(exp),
    aggregateRating: buildAggregateRating(exp),
  };
};

export const useTourCardListSchemaOrg = (expList: ComputedRef<Experience[]>, listName: ComputedRef<string>) => {
  const schema = computed<SchemaObject | null>(() => {
    if (!expList.value.length) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: listName.value,
      itemListElement: expList.value.map((exp, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: buildProduct(exp),
      })),
    };
  });

  useHead({
    script: computed(() =>
      schema.value
        ? [
            {
              type: 'application/ld+json',
              innerHTML: stringifyJsonLd(schema.value),
              key: 'schema-tour-card-list',
              tagPosition: 'bodyClose',
            },
          ]
        : [],
    ),
  });
};
